import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';

import type { AccountBody, EntryBody } from '@saldo/contracts';

import { describeSaveState, invitesResave, needsReconciliation, type SaveState } from './save';
import {
  INSTALLMENT_SCOPES,
  parseInstallmentCount,
  type InstallmentGroupView,
  type InstallmentPreviewView,
  type InstallmentScope,
} from './installments';
import { blankEntryForm, formForEntry, type EntryForm, type EntryKind } from './forms';
import { formatMoney } from './money';
import {
  carriesCategory,
  reuseSuggestion,
  toEntryDelete,
  toEntryDuplicate,
  type LifecyclePreviewView,
} from './lifecycle';
import { categoryChoiceFrom, categoryChoiceValue, categoriesFor, categoryViews } from './categories';
import type { CategoryBody, EntrySuggestionBody } from '@saldo/contracts';
import { LifecycleReview, SuggestionPicker } from './EntryLifecycle';

/**
 * The common cash/card entry editor (P4-T2; `UX-FLOWS.md` §4).
 *
 * One editor for an ordinary cash or card income/expense, and the same editor edits an
 * existing row. The account field is offered only on a create — an entry's currency is its
 * account's currency, and moving an entry between accounts is not a metadata edit, so on an
 * edit the account is shown as fact rather than as a control.
 *
 * The form's own state lives here, which is what makes a draft survive an allowed failure:
 * a refusal or an unknown outcome leaves the editor mounted with every field exactly as it
 * was typed, and nothing is written to storage. The one thing the editor does not own is
 * whether a save may be attempted again — that is the save state's decision, and it is why
 * a *saved-but-not-refreshed* entry shows no resave affordance.
 */

export interface EntryEditorProps {
  /** `null` when creating; the row being edited otherwise. */
  readonly entry: EntryBody | null;
  readonly accounts: readonly AccountBody[];
  readonly defaultAccountId: string;
  /** The server's business day, used only as a date default. */
  readonly businessToday: string;
  readonly saveState: SaveState;
  readonly saveMessage: string;
  readonly localError: string | null;
  /** The `Idempotency-Key` the last attempt carried, and whether it was a reuse. */
  readonly operationKey: { readonly key: string; readonly reused: boolean } | null;
  /** The installment count typed on a create; `1` is an ordinary entry, not a group. */
  readonly installmentCount: string;
  onChangeInstallmentCount(value: string): void;
  /** Non-null when the row being edited is a member of an installment group. */
  readonly installmentGroup: InstallmentGroupView | null;
  readonly installmentScope: InstallmentScope;
  onChangeInstallmentScope(scope: InstallmentScope): void;
  /** The stored preview awaiting confirmation, or `null` before one was taken. */
  readonly installmentPreview: InstallmentPreviewView | null;
  readonly installmentPreviewError: string | null;
  readonly installmentBusy: boolean;
  onConfirmInstallments(): void;
  onDeleteInstallments(): void;
  onReconcileInstallments(): void;
  onSubmit(form: EntryForm): void;
  /** The cash pay: the entry PATCH that sets paid and moves the due date. */
  onPay(paymentDate: string): void;
  /* ----- categories and the entry lifecycle (P7-T1) ----- */

  /** The owner's categories, archived ones included — the editor decides what to offer. */
  readonly categories: readonly CategoryBody[];
  /** The autocomplete read the workspace owns, because it is a query. */
  readonly suggestion: SuggestionState;
  onSuggestionQuery(query: string): void;
  /** The stored lifecycle preview awaiting confirmation, or `null`. */
  readonly lifecycle: LifecycleState;
  onReviewLifecycle(action: 'delete' | 'duplicate'): void;
  onAcknowledgeLifecycle(value: boolean): void;
  onConfirmLifecycle(): void;
  /** The reversible cancellation: the same control cancels and restores. */
  onToggleCancellation(): void;
  onCancel(): void;
}

export interface SuggestionState {
  readonly query: string;
  readonly items: readonly EntrySuggestionBody[];
  readonly busy: boolean;
  readonly error: string | null;
}

export interface LifecycleState {
  readonly preview: LifecyclePreviewView | null;
  readonly error: string | null;
  readonly busy: boolean;
  /** The user's own read of the impact; nothing sends `true` without it. */
  readonly acknowledged: boolean;
}

export function EntryEditor(props: EntryEditorProps) {
  const { entry, accounts, defaultAccountId, businessToday, saveState } = props;
  const formId = useId();
  const editing = entry !== null;

  const accountOf = (id: string): AccountBody | undefined =>
    accounts.find((account) => account.id === id);

  const [form, setForm] = useState<EntryForm>(() =>
    entry === null ? blankEntryForm(defaultAccountId, businessToday) : formForEntry(entry, entry.accountId),
  );
  const [paymentDate, setPaymentDate] = useState(businessToday);
  /** Whether the user has chosen an account themselves — a suggestion may not overwrite that. */
  const [accountTouched, setAccountTouched] = useState(false);
  /** What the last picked suggestion filled and what it kept, shown rather than assumed. */
  const [suggestionNote, setSuggestionNote] = useState<string | null>(null);

  // The editor follows the row it was opened for: opening another row replaces the draft.
  const openedFor = useRef(entry?.id ?? null);
  useEffect(() => {
    const id = entry?.id ?? null;
    if (id === openedFor.current) return;
    openedFor.current = id;
    setAccountTouched(false);
    setSuggestionNote(null);
    setForm(
      entry === null
        ? blankEntryForm(defaultAccountId, businessToday)
        : formForEntry(entry, entry.accountId),
    );
  }, [entry, defaultAccountId, businessToday]);

  const role = useMemo(
    () => accountOf(form.accountId)?.kind ?? accountOf(defaultAccountId)?.kind ?? 'cash',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, form.accountId, defaultAccountId],
  );
  const currency = accountOf(form.accountId)?.currency ?? accountOf(defaultAccountId)?.currency ?? '';

  // A group member's editable set is the group edit's writable set — amount, description and
  // account — so its dates and paid state are shown as fact rather than as controls (`INST-05`).
  const member = props.installmentGroup !== null && entry !== null;
  const countParsed = parseInstallmentCount(props.installmentCount);
  const groupCreate = !editing && countParsed.ok && countParsed.body > 1;

  const pending = saveState === 'saving' || props.installmentBusy;
  const locked = saveState === 'unknown';
  const canResave = invitesResave(saveState) || saveState === 'idle';
  const reconcile = needsReconciliation(saveState);

  function update(patch: Partial<EntryForm>): void {
    setForm((current) => ({ ...current, ...patch }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending || locked) return;
    props.onSubmit(form);
  }

  const cashPayable =
    editing && role === 'cash' && entry !== null && entry.type !== 'opening' && entry.type !== 'transfer' && entry.paid !== true;

  // The one optional category belongs to an ordinary row: a special row — an opening, a transfer
  // leg — carries none, and an installment member's edit is the forward-edit spine, so the field
  // is absent there and the editor says why rather than offering a control the API would refuse.
  const categoryApplies = !member && (entry === null || carriesCategory(entry));
  const activeCategories = useMemo(
    () => categoriesFor(categoryViews(props.categories), false),
    [props.categories],
  );
  const archivedCategories = useMemo(
    () => categoriesFor(categoryViews(props.categories), true),
    [props.categories],
  );
  // The two lifecycle requests are mapped here, so a row the API would refuse shows the refusal
  // instead of a control that fails on submit.
  const deleteMapping = entry === null ? null : toEntryDelete(entry);
  const duplicateMapping = entry === null ? null : toEntryDuplicate(entry);

  /**
   * Apply a picked suggestion to this draft.
   *
   * The reuse rule is `./lifecycle.ts`'s (`reuseSuggestion`), and this is the only place it is
   * called: the draft lives here, so the editor is what can fill it. What the pick kept is shown
   * rather than dropped — a suggestion never silently replaces the account, the amount or the
   * dates. On an edit the account is a fact rather than a control, so it is always "kept".
   */
  function applySuggestion(suggestion: EntrySuggestionBody): void {
    const reuse = reuseSuggestion(form, suggestion, {
      accountTouched: editing || accountTouched,
      // The names the note needs are already in hand here: the account list and the owner's
      // categories. An id neither list knows keeps its id in the note.
      labelOf: (field, id) =>
        field === 'account'
          ? accounts.find((account) => account.id === id)?.name
          : props.categories.find((category) => category.id === id)?.name,
    });
    setForm(reuse.form);
    setSuggestionNote(reuse.note);
  }

  return (
    <div className="editor" data-testid="entry-editor">
      <button
        type="button"
        className="editor-backdrop"
        aria-label="Close the editor"
        data-testid="editor-backdrop"
        onClick={props.onCancel}
        disabled={pending}
      />
      <form className="editor-panel" onSubmit={handleSubmit} noValidate>
        <header>
          <h2 id={`${formId}-title`} data-testid="editor-title">
            {editing ? 'Edit entry' : 'New entry'}
          </h2>
          <button type="button" data-testid="editor-cancel" onClick={props.onCancel} disabled={pending}>
            Close
          </button>
        </header>

        <div className="editor-fields">
          <label className="field">
            Type
            <select
              data-testid="editor-kind"
              value={form.kind}
              disabled={pending || locked || editing}
              onChange={(event) => update({ kind: event.target.value as EntryKind })}
            >
              <option value="expense">Expense</option>
              <option value="income">Income / refund</option>
            </select>
          </label>

          <label className="field">
            {`Amount (${currency})`}
            <input
              data-testid="editor-amount"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={form.amount}
              disabled={pending || locked}
              onChange={(event) => update({ amount: event.target.value })}
            />
          </label>

          <label className="field">
            Description
            <input
              data-testid="editor-description"
              autoComplete="off"
              maxLength={500}
              value={form.description}
              disabled={pending || locked}
              onChange={(event) => update({ description: event.target.value })}
            />
          </label>

          {!member ? (
            <SuggestionPicker
              query={props.suggestion.query}
              items={props.suggestion.items}
              busy={props.suggestion.busy}
              error={props.suggestion.error}
              note={suggestionNote}
              disabled={pending || locked}
              onQueryChange={props.onSuggestionQuery}
              onPick={applySuggestion}
            />
          ) : null}

          {categoryApplies ? (
            <label className="field">
              Category
              <select
                data-testid="editor-category"
                value={categoryChoiceValue(
                  form.categoryId === null ? { kind: 'none' } : { kind: 'category', id: form.categoryId },
                )}
                disabled={pending || locked}
                onChange={(event) => {
                  const choice = categoryChoiceFrom(event.target.value);
                  update({ categoryId: choice.kind === 'none' ? null : choice.id });
                }}
              >
                <option value="">Uncategorized</option>
                {activeCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
                {archivedCategories.length !== 0 ? (
                  <optgroup label="Archived — keeps its history">
                    {archivedCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </select>
            </label>
          ) : editing ? (
            <p className="hint" data-testid="editor-category-none">
              {`A ${entry?.type ?? 'special'} row carries no category, so none is offered.`}
            </p>
          ) : null}

          {editing && !member ? (
            <p className="hint" data-testid="editor-account-fixed">
              Account: {accountOf(entry?.accountId ?? '')?.name ?? '—'} · {currency}
            </p>
          ) : (
            <label className="field">
              Account
              <select
                data-testid="editor-account"
                value={form.accountId}
                disabled={pending || locked}
                onChange={(event) => {
                  setAccountTouched(true);
                  update({ accountId: event.target.value });
                }}
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} · {account.currency}
                  </option>
                ))}
              </select>
            </label>
          )}

          {!editing ? (
            <label className="field">
              Installments
              <input
                data-testid="editor-installments"
                inputMode="numeric"
                autoComplete="off"
                value={props.installmentCount}
                disabled={pending || locked}
                onChange={(event) => props.onChangeInstallmentCount(event.target.value)}
              />
              {!countParsed.ok ? (
                <span className="hint" data-testid="editor-installments-error">
                  {countParsed.error}
                </span>
              ) : null}
            </label>
          ) : null}

          {member ? (
            <>
              <p className="hint" data-testid="editor-member-dates">
                Scheduled {entry?.dueDate ?? '—'} · this edit carries no date, no paid state and no
                note, so none of them can be propagated to another installment.
              </p>
            </>
          ) : (
            <div className="two-fields">
              <label className="field">
                Transaction date
                <input
                  type="date"
                  data-testid="editor-transaction"
                  value={form.transactionDate}
                  disabled={pending || locked}
                  onChange={(event) => update({ transactionDate: event.target.value })}
                />
              </label>
              <label className="field">
                Due date
                <input
                  type="date"
                  data-testid="editor-due"
                  value={form.dueDate}
                  disabled={pending || locked}
                  onChange={(event) => update({ dueDate: event.target.value })}
                />
              </label>
            </div>
          )}

          {member ? (
            <label className="field">
              Apply to
              <select
                data-testid="editor-scope"
                value={props.installmentScope}
                disabled={pending || locked}
                onChange={(event) => props.onChangeInstallmentScope(event.target.value as InstallmentScope)}
              >
                {INSTALLMENT_SCOPES.map((scope) => (
                  <option key={scope} value={scope}>
                    {scope === 'single' ? 'This installment only' : 'This and later installments'}
                  </option>
                ))}
              </select>
            </label>
          ) : role === 'cash' ? (
            <label className="field">
              Cash state
              <select
                data-testid="editor-paid"
                value={form.paid ? 'true' : 'false'}
                disabled={pending || locked}
                onChange={(event) => update({ paid: event.target.value === 'true' })}
              >
                <option value="true">Already paid / received</option>
                <option value="false">Unpaid / pending</option>
              </select>
            </label>
          ) : (
            <p className="hint" data-testid="editor-card-note">
              A card entry is effective on its due date. This does not mean the issuer has been
              paid.
            </p>
          )}
        </div>

        {cashPayable ? (
          <div className="two-fields">
            <label className="field">
              Mark paid on
              <input
                type="date"
                data-testid="editor-pay-date"
                value={paymentDate}
                disabled={pending || locked}
                onChange={(event) => setPaymentDate(event.target.value)}
              />
            </label>
            <p className="field">
              Cash pay
              <button
                type="button"
                data-testid="editor-mark-paid"
                disabled={pending || locked}
                onClick={() => props.onPay(paymentDate)}
              >
                Mark this entry paid
              </button>
            </p>
          </div>
        ) : null}

        {props.localError !== null ? (
          <p className="status-bad" role="alert" data-testid="editor-error">
            {props.localError}
          </p>
        ) : null}

        <p
          role="status"
          data-testid="editor-status"
          data-save-state={saveState}
          className={saveState === 'not-saved' || saveState === 'unknown' ? 'notice warn' : 'notice'}
          hidden={saveState === 'idle'}
        >
          {props.saveMessage === '' ? describeSaveState(saveState) : props.saveMessage}
        </p>

        {props.operationKey !== null ? (
          <p className="hint">
            Operation key{' '}
            <code data-testid="editor-operation-key" data-reused={props.operationKey.reused ? 'true' : 'false'}>
              {props.operationKey.key}
            </code>
          </p>
        ) : null}

        {props.installmentPreviewError !== null ? (
          <p className="status-bad" role="alert" data-testid="editor-preview-error">
            {props.installmentPreviewError}
          </p>
        ) : null}

        {props.installmentPreview !== null ? (
          <section
            className="preview"
            data-testid="editor-preview"
            data-action={props.installmentPreview.action}
            aria-label="Installment preview"
          >
            <h3 data-testid="editor-preview-title">
              {props.installmentPreview.action === 'installments.delete'
                ? 'Deletion preview'
                : props.installmentPreview.action === 'installments.edit'
                  ? 'Edit preview'
                  : 'Generation preview'}
            </h3>
            <p className="hint">
              <span data-testid="editor-preview-count">
                {props.installmentPreview.activeCount} active of {props.installmentPreview.scheduledCount}{' '}
                scheduled
              </span>{' '}
              · revised total{' '}
              <b className="money" data-testid="editor-preview-total">
                {formatMoney(BigInt(props.installmentPreview.totalMinor), props.installmentPreview.currency)}
              </b>{' '}
              · decided on {props.installmentPreview.businessToday}
            </p>
            <ul className="preview-list" data-testid="editor-preview-members">
              {props.installmentPreview.lines.map((line) => (
                <li
                  key={line.entryId ?? `new-${line.ordinal}`}
                  data-testid="editor-preview-member"
                  data-ordinal={line.ordinal}
                >
                  <span>{line.label}</span>
                  <span data-testid="editor-preview-due">{line.dueDate}</span>
                  <span className={`money${BigInt(line.signedMinor) < 0n ? ' negative' : ''}`}>
                    {formatMoney(BigInt(line.signedMinor), line.currency)}
                  </span>
                </li>
              ))}
            </ul>
            {props.installmentPreview.exclusions.length > 0 ? (
              <ul className="preview-excluded" data-testid="editor-preview-excluded">
                {props.installmentPreview.exclusions.map((excluded) => (
                  <li
                    key={excluded.entryId}
                    data-testid="editor-preview-exclusion"
                    data-reason={excluded.reason}
                  >
                    {excluded.label}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}

        {editing && !member && entry !== null ? (
          <LifecycleReview
            deleteBlocked={deleteMapping !== null && !deleteMapping.ok ? deleteMapping.error : null}
            duplicateBlocked={
              duplicateMapping !== null && !duplicateMapping.ok ? duplicateMapping.error : null
            }
            canceled={entry.canceled}
            preview={props.lifecycle.preview}
            error={props.lifecycle.error}
            busy={props.lifecycle.busy}
            acknowledged={props.lifecycle.acknowledged}
            onReview={props.onReviewLifecycle}
            onAcknowledge={props.onAcknowledgeLifecycle}
            onConfirm={props.onConfirmLifecycle}
            onToggleCancellation={props.onToggleCancellation}
          />
        ) : null}

        <footer className="editor-footer">
          <button
            type="submit"
            className="primary"
            data-testid="editor-save"
            disabled={pending || locked || !canResave}
          >
            {pending
              ? 'Saving…'
              : member
                ? 'Preview this edit'
                : groupCreate
                  ? `Preview ${props.installmentCount.trim()} installments`
                  : editing
                    ? 'Save entry'
                    : 'Create entry'}
          </button>
          {groupCreate || member ? (
            <button
              type="button"
              data-testid="editor-confirm"
              disabled={pending || locked || props.installmentPreview === null}
              onClick={props.onConfirmInstallments}
            >
              {props.installmentPreview?.action === 'installments.delete'
                ? 'Confirm deletion'
                : 'Confirm this change'}
            </button>
          ) : null}
          {member ? (
            <button
              type="button"
              data-testid="editor-delete"
              disabled={pending || locked}
              onClick={props.onDeleteInstallments}
            >
              Delete this installment
            </button>
          ) : null}
          {reconcile ? (
            <button
              type="button"
              data-testid="editor-retry"
              onClick={groupCreate || member ? props.onReconcileInstallments : () => props.onSubmit(form)}
            >
              Reconcile this intent
            </button>
          ) : null}
        </footer>
      </form>
    </div>
  );
}

/** The account editor: a name, a currency, a role, and an optional signed/zero opening. */
export interface AccountEditorProps {
  readonly businessToday: string;
  readonly saveState: SaveState;
  readonly saveMessage: string;
  readonly localError: string | null;
  onSubmit(form: {
    name: string;
    currency: 'BRL' | 'CAD' | 'USD';
    role: 'cash' | 'card';
    withOpening: boolean;
    openingDate: string;
    openingAmount: string;
  }): void;
  onCancel(): void;
}

export function AccountEditor(props: AccountEditorProps) {
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<'BRL' | 'CAD' | 'USD'>('CAD');
  const [role, setRole] = useState<'cash' | 'card'>('cash');
  const [withOpening, setWithOpening] = useState(false);
  const [openingDate, setOpeningDate] = useState(props.businessToday);
  const [openingAmount, setOpeningAmount] = useState('0.00');
  const pending = props.saveState === 'saving';

  return (
    <div className="editor" data-testid="account-editor">
      <button
        type="button"
        className="editor-backdrop"
        aria-label="Close the editor"
        data-testid="account-editor-backdrop"
        onClick={props.onCancel}
        disabled={pending}
      />
      <form
        className="editor-panel"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (pending) return;
          props.onSubmit({ name, currency, role, withOpening, openingDate, openingAmount });
        }}
      >
        <header>
          <h2 data-testid="account-editor-title">New account</h2>
          <button type="button" data-testid="account-editor-cancel" onClick={props.onCancel} disabled={pending}>
            Close
          </button>
        </header>

        <div className="editor-fields">
          <label className="field">
            Name
            <input
              data-testid="account-name"
              autoComplete="off"
              maxLength={120}
              value={name}
              disabled={pending}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <div className="two-fields">
            <label className="field">
              Currency
              <select
                data-testid="account-currency"
                value={currency}
                disabled={pending}
                onChange={(event) => setCurrency(event.target.value as 'BRL' | 'CAD' | 'USD')}
              >
                <option value="BRL">BRL</option>
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
              </select>
            </label>
            <label className="field">
              Kind
              <select
                data-testid="account-kind"
                value={role}
                disabled={pending}
                onChange={(event) => setRole(event.target.value as 'cash' | 'card')}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
              </select>
            </label>
          </div>

          <label className="field">
            <input
              type="checkbox"
              data-testid="account-with-opening"
              checked={withOpening}
              disabled={pending}
              style={{ width: 'auto', minHeight: 0 }}
              onChange={(event) => setWithOpening(event.target.checked)}
            />{' '}
            Add a signed opening
          </label>

          {withOpening ? (
            <div className="two-fields">
              <label className="field">
                Opening date
                <input
                  type="date"
                  data-testid="account-opening-date"
                  value={openingDate}
                  disabled={pending}
                  onChange={(event) => setOpeningDate(event.target.value)}
                />
              </label>
              <label className="field">
                Opening amount (signed or zero)
                <input
                  data-testid="account-opening-amount"
                  inputMode="decimal"
                  autoComplete="off"
                  value={openingAmount}
                  disabled={pending}
                  onChange={(event) => setOpeningAmount(event.target.value)}
                />
              </label>
            </div>
          ) : null}
        </div>

        {props.localError !== null ? (
          <p className="status-bad" role="alert" data-testid="account-editor-error">
            {props.localError}
          </p>
        ) : null}

        <p role="status" data-testid="account-editor-status" data-save-state={props.saveState} hidden={props.saveState === 'idle'}>
          {props.saveMessage}
        </p>

        <footer className="editor-footer">
          <button type="submit" className="primary" data-testid="account-editor-save" disabled={pending}>
            {pending ? 'Saving…' : 'Create account'}
          </button>
        </footer>
      </form>
    </div>
  );
}

/** Format an entry's amount for a row: the magnitude with its type's sign. */
export function rowAmount(entry: EntryBody): bigint {
  return BigInt(entry.signedMinor);
}
